import React, { useEffect, useState } from 'react';
import { OrderResponse, TraderDevice, ordersAPI, traderDevicesAPI } from '../../lib/api';
import { kgsBankId } from '../../lib/kgsBanks';
import { Switch } from '../ui/switch';

export function OrderAutomation({ order, onUpdated }: { order: OrderResponse; onUpdated: () => void }) {
    const [devices, setDevices] = useState<TraderDevice[]>([]);
    const [selected, setSelected] = useState(order.automation?.device_id || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);
    const bank = kgsBankId(order.recipient_bank);
    const types = order.requisite_types || (order.recipient_card ? ['card'] : []);
    const cardSupported = types.includes('card') && !!order.recipient_card;
    const elqrSupported = ['optima', 'mbank', 'obank', 'kompanion'].includes(bank || '') && types.includes('elqr') && !!order.qr_url;
    const sbpSupported = ['optima', 'mbank', 'obank', 'kompanion'].includes(bank || '') && types.includes('sbp')
        && !!order.phone_number?.replace(/\D/g, '');
    const supported = order.order_type === 'deposit' && order.from_currency === 'KGS'
        && ['optima', 'mbank', 'obank', 'kompanion'].includes(bank || '')
        && (cardSupported || elqrSupported || sbpSupported);
    useEffect(() => {
        if (!supported) return;
        let active = true;
        const refresh = () => traderDevicesAPI.list()
            .then(items => { if (active) { setDevices(items); setLoaded(true); } })
            .catch(() => { if (active) setError('Не удалось загрузить устройства'); });
        refresh();
        const timer = setInterval(refresh, 20000);
        return () => { active = false; clearInterval(timer); };
    }, [supported, order.automation?.version]);
    useEffect(() => { setSelected(order.automation?.device_id || ''); }, [order.automation?.device_id]);
    const bindingRevoked = devices.some(item => item.id === order.automation?.device_id && item.status === 'revoked');
    useEffect(() => {
        if (devices.some(item => item.id === selected && item.status === 'revoked')) setSelected('');
    }, [devices, selected]);
    const enabled = !!order.automation?.enabled && !bindingRevoked;
    const device = devices.find(item => item.id === selected);
    const deviceActive = device?.status === 'active';
    const toggle = async (value: boolean) => {
        setBusy(true); setError('');
        try { await ordersAPI.setAutomation(order.id, value, selected || undefined); onUpdated(); }
        catch (err: any) { setError(err.response?.data?.detail || 'Не удалось изменить автоматику'); }
        finally { setBusy(false); }
    };
    return <div className="w-full min-w-0 space-y-2 break-words rounded border p-3 text-sm">
        <label className="flex items-center justify-between gap-3">
            <span>Автоматика</span>
            <Switch checked={enabled} onCheckedChange={toggle}
                disabled={busy || (!enabled && (!supported || !deviceActive))} />
        </label>
        {!supported ? <p className="text-xs text-gray-500 dark:text-slate-400">KGS: card, ELQR и sbp_kgs — Optima, MBANK, О!Банк, Компаньон</p> : <>
            <select aria-label="Устройство для автоматики" className="w-full min-w-0 max-w-full rounded border p-1"
                value={selected} disabled={busy || enabled} onChange={e => setSelected(e.target.value)}>
                <option value="">Выберите устройство</option>
                {devices.filter(d => d.status === 'active').map(d =>
                    <option key={d.id} value={d.id} disabled={d.status !== 'active'}>
                        {d.name}{d.status !== 'active' ? ' — отключено' : ''}
                    </option>)}
            </select>
            {bindingRevoked && <p className="text-xs text-amber-700 dark:text-amber-300">Устройство отвязано. Выберите другое устройство и включите автоматику заново.</p>}
            {loaded && selected && !deviceActive && <p className="text-xs text-amber-700 dark:text-amber-300">Устройство отключено. Автоподтверждение недоступно.</p>}
            {loaded && deviceActive && !device?.online && <p className="text-xs text-amber-700 dark:text-amber-300">Телефон не в сети. Автоподтверждение ожидает подключения до истечения заявки.</p>}
            <p className="text-xs text-gray-500 dark:text-slate-400">Для новых заявок: {[cardSupported && 'card', elqrSupported && 'ELQR', sbpSupported && 'sbp_kgs'].filter(Boolean).join(', ')}.</p>
        </>}
        {error && <p role="alert" className="text-xs text-red-600 dark:text-red-300">{error}</p>}
    </div>;
}
